import { useCallback, useEffect, useState } from 'react';
import {
  type AchievementPatch,
  type AchievementRecord,
  type RolePatch,
  type RoleRecord,
  getCorpusAchievements,
  getCorpusRoles,
  rehomeAchievement,
  updateAchievement,
  updateRole,
} from './api';
import {
  INITIAL_DRAG_REHOME_STATE,
  applyOptimisticRehome,
  confirmRehome,
  rollbackRehome,
} from './dragRehome';
import {
  applyOptimisticEdit,
  confirmFieldEdit,
  initialFieldEditState,
  rollbackFieldEdit,
  type FieldEditState,
} from './fieldEdit';

/**
 * The Refine tab's corpus-canvas data hook — the Throughline analogue of
 * apps/web/src/lib/useCorpus.ts, scoped to exactly what the card canvas needs.
 * Originally (B-3) read-only + drag-rehome; LANE ROLE-REFINE adds in-place
 * field editing for both roles and achievements (editRole/editAchievement
 * below) on top of the SAME PATCH routes apps/web's EditRoleSheet/
 * EditAchievementSheet already call — this hook is the first Throughline
 * consumer of them. Still does not port useCorpus's full surface (quick-add,
 * merge, reframe, delete, reposition, tag editing) — those remain real
 * backend routes not yet wired into this app; extending further is additive,
 * not a rewrite.
 */

export interface CorpusCanvasState {
  roles: RoleRecord[];
  achievements: AchievementRecord[];
  loading: boolean;
  error: string | null;
}

export function useCorpusCanvas() {
  const [state, setState] = useState<CorpusCanvasState>({
    roles: [],
    achievements: [],
    loading: true,
    error: null,
  });
  const [dragState, setDragState] = useState(INITIAL_DRAG_REHOME_STATE);
  const [roleEditState, setRoleEditState] = useState<FieldEditState<RoleRecord>>(() =>
    initialFieldEditState<RoleRecord>(),
  );
  const [achievementEditState, setAchievementEditState] = useState<
    FieldEditState<AchievementRecord>
  >(() => initialFieldEditState<AchievementRecord>());

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const [roles, achievements] = await Promise.all([
        getCorpusRoles(),
        getCorpusAchievements(),
      ]);
      setState({ roles, achievements, loading: false, error: null });
    } catch (err) {
      setState((s) => ({
        ...s,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
      }));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * The drag-rehome move (brief B-3.b): optimistic move -> PUT rehome ->
   * server-confirm (re-fetch, since a supersede mints a new id the optimistic
   * patch can't know) -> error rollback (restore the pre-drag snapshot). See
   * dragRehome.ts's header for why this is a deliberate departure from
   * apps/web's own (non-optimistic) drag handler.
   */
  const rehome = useCallback(
    async (cardId: string, targetRoleId: string | null, targetRoleEventId: string | null = null) => {
      const card = state.achievements.find((a) => a.id === cardId);
      if (!card) return;
      // No-op guard: dropping on the era the card already lives in changes
      // nothing — skip the round trip entirely (mirrors apps/web's sameEra
      // check, Corpus.tsx:438-441).
      const sameEra =
        (targetRoleId ?? null) === (card.roleId ?? null) &&
        (targetRoleEventId ?? null) === (card.roleEventId ?? null);
      if (sameEra) return;

      const { next, state: nextDragState } = applyOptimisticRehome(
        state.achievements,
        cardId,
        targetRoleId,
        targetRoleEventId,
      );
      setState((s) => ({ ...s, achievements: next }));
      setDragState(nextDragState);

      try {
        await rehomeAchievement(cardId, {
          ...(targetRoleEventId != null
            ? { targetRoleEventId }
            : { targetRoleId: targetRoleId ?? undefined }),
          sourceRef: {
            kind: 'drag_drop',
            capturedFrom: `Dragged "${card.headline}" to ${targetRoleId ? roleLabel(state.roles, targetRoleId) : 'Unattached'}`,
          },
        });
        setDragState(confirmRehome());
        // Re-fetch: the supersede minted a NEW row id server-side. The
        // optimistic patch kept the OLD id in place (visually correct — the
        // card is still where the user dropped it) but the canvas must pick
        // up the real id + any server-side fields (destinationGrounded etc.)
        // on the next read, same served-truth law apps/web's canvas follows.
        await refresh();
      } catch (err) {
        const { restored } = rollbackRehome(nextDragState);
        setDragState(INITIAL_DRAG_REHOME_STATE);
        if (restored) setState((s) => ({ ...s, achievements: restored }));
        return err instanceof Error ? err.message : 'Could not move that card — try again.';
      }
      return null;
    },
    [state.achievements, state.roles, refresh],
  );

  const getRoleId = useCallback((r: RoleRecord) => r.id, []);
  const getAchievementId = useCallback((a: AchievementRecord) => a.id, []);

  /**
   * Edit a role's own fields (title/company/dates/location/etc — parity
   * matrix gap #6). Optimistic patch -> PATCH /corpus/roles/:id ->
   * server-confirm (the PATCH result REPLACES the optimistic row rather than
   * triggering a full refresh, since a role edit — unlike a rehome — never
   * mints a new id; the server's row is simply the more authoritative one,
   * e.g. isCurrent's boolean->0|1 coercion) -> error rollback. Same
   * optimistic+rollback discipline as `rehome` above, applied via fieldEdit.ts
   * (the generic version of dragRehome.ts's own three-function shape).
   */
  const editRole = useCallback(
    async (id: string, patch: RolePatch) => {
      const { next, state: nextEditState } = applyOptimisticEdit(
        state.roles,
        id,
        getRoleId,
        patch as Partial<RoleRecord>,
      );
      setState((s) => ({ ...s, roles: next }));
      setRoleEditState(nextEditState);

      try {
        const serverRole = await updateRole(id, patch);
        const { next: confirmed, state: confirmedState } = confirmFieldEdit(
          next,
          id,
          getRoleId,
          serverRole,
        );
        setState((s) => ({ ...s, roles: confirmed }));
        setRoleEditState(confirmedState);
      } catch (err) {
        const { restored, state: rolledBack } = rollbackFieldEdit(next, getRoleId, nextEditState);
        setRoleEditState(rolledBack);
        if (restored) setState((s) => ({ ...s, roles: restored }));
        return err instanceof Error ? err.message : 'Could not save that role — try again.';
      }
      return null;
    },
    [state.roles, getRoleId],
  );

  /**
   * Edit an achievement's own fields (headline/STAR/kpi — parity matrix gap
   * #7). Same optimistic+confirm+rollback discipline as editRole above,
   * against PATCH /corpus/achievements/:id. Distinct from `rehome`, which
   * moves a card BETWEEN roles via the §6 supersede (a different route,
   * different semantics — a rehome can mint a new id; a field edit never
   * does, since it doesn't touch roleId/roleEventId).
   */
  const editAchievement = useCallback(
    async (id: string, patch: AchievementPatch) => {
      const { next, state: nextEditState } = applyOptimisticEdit(
        state.achievements,
        id,
        getAchievementId,
        patch as Partial<AchievementRecord>,
      );
      setState((s) => ({ ...s, achievements: next }));
      setAchievementEditState(nextEditState);

      try {
        const serverAchievement = await updateAchievement(id, patch);
        const { next: confirmed, state: confirmedState } = confirmFieldEdit(
          next,
          id,
          getAchievementId,
          serverAchievement,
        );
        setState((s) => ({ ...s, achievements: confirmed }));
        setAchievementEditState(confirmedState);
      } catch (err) {
        const { restored, state: rolledBack } = rollbackFieldEdit(
          next,
          getAchievementId,
          nextEditState,
        );
        setAchievementEditState(rolledBack);
        if (restored) setState((s) => ({ ...s, achievements: restored }));
        return err instanceof Error ? err.message : 'Could not save that card — try again.';
      }
      return null;
    },
    [state.achievements, getAchievementId],
  );

  return {
    state,
    dragState,
    refresh,
    rehome,
    roleEditState,
    achievementEditState,
    editRole,
    editAchievement,
  };
}

function roleLabel(roles: RoleRecord[], roleId: string): string {
  const role = roles.find((r) => r.id === roleId);
  return role ? `${role.company} — ${role.title}` : roleId;
}
